/**
 * Feature hook: Documents + FilteredDocuments + IfsLatestDocument state and document thunks.
 */
import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../hooks";
import {
  onPageChange,
  onIFSDocumentPageChange,
  getDocClasses,
  getDocClassFormats,
  getRecentDocumentForUser,
  onSetDocumentInitialState,
  onSearchDocument,
  onSearchDocumentCount,
} from "../slices/documentsAction";

export function useDocuments() {
  const dispatch = useAppDispatch();
  const documents = useAppSelector((state) => state.Documents);
  const filteredDocuments = useAppSelector((state) => state.FilteredDocuments);
  const ifsLatestDocument = useAppSelector((state) => state.IfsLatestDocument);
  const filteredOneDriveDocuments = useAppSelector(
    (state) => state.FilteredOneDriveDocuments
  );

  const onPageChangeAction = useCallback(
    (documents, oneDriveDocuments, selectedPageIndex, pageSize) =>
      dispatch(onPageChange(documents, oneDriveDocuments, selectedPageIndex, pageSize)),
    [dispatch]
  );
  const onIFSDocumentPageChangeAction = useCallback(
    (documents, oneDriveDocuments, selectedPageIndex, pageSize) =>
      dispatch(onIFSDocumentPageChange(documents, oneDriveDocuments, selectedPageIndex, pageSize)),
    [dispatch]
  );
  const getDocClassesAction = useCallback(
    (authChannel) => dispatch(getDocClasses(authChannel)),
    [dispatch]
  );
  const getDocClassFormatsAction = useCallback(
    (authChannel) => dispatch(getDocClassFormats(authChannel)),
    [dispatch]
  );
  const getRecentDocumentForUserAction = useCallback(
    (...args) => dispatch(getRecentDocumentForUser(...args)),
    [dispatch]
  );
  const onSetDocumentInitialStateAction = useCallback(
    () => dispatch(onSetDocumentInitialState()),
    [dispatch]
  );
  /**
   * Mirror of legacy document search thunk signature.
   * Keep parameter order aligned with `onSearchDocument` until the thunk is refactored to an object payload.
   */
  const onSearchDocumentAction = useCallback(
    (
      searchValue,
      searchBy,
      pageSize,
      searchType,
      advanceSearchReleaseLevel,
      advanceSearchStartDate,
      advanceSearchEndDate,
      advanceSearchOnlyUserInvolved,
      advanceSearchAnyDate,
      advanceSearchDocClass,
      advanceSearchFormat,
      externalSearch,
      docRev,
      fileStatus,
      tenantId,
      groupId,
      channelId,
      lastCheckOutMe,
      partiallyCompleted,
      docType,
      cachedDocuments,
      isBackgroundLoad,
      pageIndex,
      loadPagingation,
      isIfsFileAccessible,
      tokenRefreshPreAction,
      actionType
    ) =>
      dispatch(
        onSearchDocument(
          searchValue,
          searchBy,
          pageSize,
          searchType,
          advanceSearchReleaseLevel,
          advanceSearchStartDate,
          advanceSearchEndDate,
          advanceSearchOnlyUserInvolved,
          advanceSearchAnyDate,
          advanceSearchDocClass,
          advanceSearchFormat,
          externalSearch,
          docRev,
          fileStatus,
          tenantId,
          groupId,
          channelId,
          lastCheckOutMe,
          partiallyCompleted,
          docType,
          cachedDocuments,
          isBackgroundLoad,
          pageIndex,
          loadPagingation,
          isIfsFileAccessible,
          tokenRefreshPreAction,
          actionType
        )
      ),
    [dispatch]
  );
  /**
   * Mirror of legacy search-count thunk signature.
   * Keep parameter order aligned with `onSearchDocumentCount` until it can be moved to an object payload.
   */
  const onSearchDocumentCountAction = useCallback(
    (
      searchValue,
      searchBy,
      pageSize,
      searchType,
      advanceSearchReleaseLevel,
      advanceSearchStartDate,
      advanceSearchEndDate,
      advanceSearchOnlyUserInvolved,
      advanceSearchAnyDate,
      advanceSearchDocClass,
      advanceSearchFormat,
      externalSearch,
      docRev,
      fileStatus,
      tenantId,
      groupId,
      channelId,
      lastCheckOutMe,
      partiallyCompleted,
      docType,
      cachedDocuments,
      isIfsFileAccessible
    ) =>
      dispatch(
        onSearchDocumentCount(
          searchValue,
          searchBy,
          pageSize,
          searchType,
          advanceSearchReleaseLevel,
          advanceSearchStartDate,
          advanceSearchEndDate,
          advanceSearchOnlyUserInvolved,
          advanceSearchAnyDate,
          advanceSearchDocClass,
          advanceSearchFormat,
          externalSearch,
          docRev,
          fileStatus,
          tenantId,
          groupId,
          channelId,
          lastCheckOutMe,
          partiallyCompleted,
          docType,
          cachedDocuments,
          isIfsFileAccessible
        )
      ),
    [dispatch]
  );

  return {
    documents,
    filteredDocuments,
    ifsLatestDocument,
    filteredOneDriveDocuments,
    onPageChange: onPageChangeAction,
    onIFSDocumentPageChange: onIFSDocumentPageChangeAction,
    getDocClasses: getDocClassesAction,
    getDocClassFormats: getDocClassFormatsAction,
    getRecentDocumentForUser: getRecentDocumentForUserAction,
    onSetDocumentInitialState: onSetDocumentInitialStateAction,
    onSearchDocument: onSearchDocumentAction,
    onSearchDocumentCount: onSearchDocumentCountAction,
  };
}
